import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import ar from 'date-fns/locale/ar';
import en from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import { TFunction } from 'next-i18next';

import { IHome } from '@/modules/home/types';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { Item } from '@/shared/components/molecules/PartialListWithTooltip/IPartialListWithTooltip';

import Farmer from '~/images/shared/Farmer.svg';

export const getCount = (list: IHome.IWorkforceDistributionItemSchema[]) => {
  return list.reduce(
    (count, currentValue) => count + currentValue.workersCount,
    0
  );
};

export const getMax = (list: IHome.IWorkforceDistributionItemSchema[]) => {
  let max = 0;
  list?.map((item) => {
    if (item.workersCount > max) max = item.workersCount;
  });
  return max;
};

const supervisorColumnHelper =
  createColumnHelper<IHome.IWorkforceResourcesSupervisorSchema>();

export const workforceResourcesSupervisorsColumns = (
  t: TFunction,
  locale: string | undefined
) => [
  supervisorColumnHelper.accessor('supervisorName', {
    cell: ({ getValue, row }) => (
      <div className='flex items-center gap-8'>
        <div className='flex h-30 w-30 items-center justify-center overflow-hidden rounded-full bg-background-neutral-100 text-18 text-primary-default'>
          <Farmer />
        </div>
        <div>
          <div className='flex text-14 font-bold '>{getValue()}</div>
          <div className='flex text-12 text-surface-10'>
            {t('last-active')}:{' '}
            {format(new Date(row.original.lastActive), 'hh:mm a', {
              locale:
                locale === 'ar'
                  ? ar
                  : locale === 'es'
                  ? es
                  : locale === 'fr'
                  ? fr
                  : en,
            })}
          </div>
        </div>
      </div>
    ),
    maxSize: 190,
    header: () => t('name'),
  }),

  supervisorColumnHelper.accessor('inProgressTaskNames', {
    cell: ({ getValue }) => {
      const val = getValue();
      return val ? (
        <PartialListWithTooltip
          limit={1}
          hasBorder={false}
          items={val.map((item) => {
            return {
              description: item,
            } as Item;
          })}
        />
      ) : (
        '-'
      );
    },
    maxSize: 190,
    header: () => <span className='capitalize'>{t('in-progress')}</span>,
  }),
  supervisorColumnHelper.accessor('locationNames', {
    cell: ({ getValue }) => {
      const val = getValue();
      return val ? (
        <PartialListWithTooltip
          limit={1}
          items={val.map((item) => {
            return {
              description: item.number,
              imageURL: item.image,
            } as Item;
          })}
        />
      ) : (
        '-'
      );
    },
    maxSize: 190,
    header: () => t('location'),
  }),
];

const crewColumnHelper =
  createColumnHelper<IHome.IWorkforceResourcesCrewSchema>();

export const workforceResourcesCrewsColumns = (t: TFunction) => [
  crewColumnHelper.accessor('contractorName', {
    cell: ({ getValue }) => (
      <div className='flex items-center gap-8'>
        <div className='flex h-32 w-32 items-center justify-center overflow-hidden rounded-full bg-background-neutral-100 text-20 text-primary-default'>
          <Farmer />
        </div>
        <div className='flex text-base font-semi-bold '>{getValue()}</div>
      </div>
    ),
    maxSize: 190,
    header: () => t('contractor-name'),
  }),

  crewColumnHelper.accessor('workersAttended', {
    cell: ({ getValue }) => getValue() ?? 0,
    maxSize: 190,
    header: () => t('workers-attending'),
  }),
  crewColumnHelper.accessor('idleWorkers', {
    cell: ({ getValue }) => getValue() ?? 0,
    maxSize: 190,
    header: () => t('idle-workers'),
  }),
];
